import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertTriangle, GitBranch, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { client } from "@/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const Route = createFileRoute("/traces/")({
  component: TracesPage,
});

async function fetchTraces() {
  const res = await client.api.traces.$get({
    query: { limit: "100" },
  });
  if (!res.ok) throw new Error("Failed to load traces");
  return res.json();
}

type TraceList = Awaited<ReturnType<typeof fetchTraces>>;
type Trace = TraceList[number];

function statusVariant(status: string) {
  switch (status) {
    case "completed":
      return "default" as const;
    case "running":
      return "secondary" as const;
    case "error":
      return "destructive" as const;
    default:
      return "outline" as const;
  }
}

function formatDuration(ms: number | null) {
  if (ms == null) return "-";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleString();
}

/** Per-million-token pricing by model prefix (longest match wins). */
const MODEL_PRICING: Record<
  string,
  { input: number; output: number; cacheRead: number; cacheWrite: number }
> = {
  // Opus 5 — $5 input, same rates as Opus 4.x
  "claude-opus-5": { input: 5, output: 25, cacheRead: 0.5, cacheWrite: 6.25 },
  // Opus 4.7 / 4.6 / 4.5 — $5 input. Kept for historical traces.
  "claude-opus-4-7": { input: 5, output: 25, cacheRead: 0.5, cacheWrite: 6.25 },
  "claude-opus-4-6": { input: 5, output: 25, cacheRead: 0.5, cacheWrite: 6.25 },
  "claude-opus-4-5": { input: 5, output: 25, cacheRead: 0.5, cacheWrite: 6.25 },
  // Opus 4.1 / 4 — $15 input
  "claude-opus-4": { input: 15, output: 75, cacheRead: 1.5, cacheWrite: 18.75 },
  // Sonnet 4.x — $3 input
  "claude-sonnet-4": { input: 3, output: 15, cacheRead: 0.3, cacheWrite: 3.75 },
  // Haiku 4.5 — $1 input
  "claude-haiku-4-5": { input: 1, output: 5, cacheRead: 0.1, cacheWrite: 1.25 },
  // Haiku 4 — $0.80 input
  "claude-haiku-4": { input: 0.8, output: 4, cacheRead: 0.08, cacheWrite: 1 },
  // Haiku 3.5 — $0.80 input
  "claude-haiku-3-5": { input: 0.8, output: 4, cacheRead: 0.08, cacheWrite: 1 },
  // Gemini 3.1 Pro Preview — kept for historical traces; $2/$4 input (<=200k /
  // >200k), lower tier as default
  "gemini-3.1-pro-preview": {
    input: 2,
    output: 12,
    cacheRead: 0.2,
    cacheWrite: 2,
  },
  // Gemini 3.7 Flash — introductory rates, doubling on 2027-01-01
  "gemini-3.7-flash": {
    input: 0.75,
    output: 3.75,
    cacheRead: 0.075,
    cacheWrite: 0.75,
  },
  // Gemini 3 Flash Preview — kept for historical traces recorded before 3.7
  "gemini-3-flash-preview": {
    input: 0.5,
    output: 3,
    cacheRead: 0.05,
    cacheWrite: 0.5,
  },
  // Gemini 3.5 Flash-Lite — $0.30 input / $2.50 output / $0.03 cache read
  "gemini-3.5-flash-lite": {
    input: 0.3,
    output: 2.5,
    cacheRead: 0.03,
    cacheWrite: 0.3,
  },
  // Qwen 3.6 (Fireworks) — $0.50 input / $3 output / $0.10 cache read
  "accounts/fireworks/models/qwen3p6": {
    input: 0.5,
    output: 3,
    cacheRead: 0.1,
    cacheWrite: 0.5,
  },
  // Kimi 2.6 (Fireworks) — $0.95 input / $0.16 cache read / $4 output
  "accounts/fireworks/models/kimi-k2p6": {
    input: 0.95,
    output: 4,
    cacheRead: 0.16,
    cacheWrite: 0.95,
  },
  // Kimi K2.7 code (Fireworks) — $0.95 input / $0.19 cache read / $4 output
  "accounts/fireworks/models/kimi-k2p7-code": {
    input: 0.95,
    output: 4,
    cacheRead: 0.19,
    cacheWrite: 0.95,
  },
  // OpenAI GPT-5.4 — kept for historical traces recorded before the 5.6 upgrade
  "gpt-5.4": { input: 2.5, output: 15, cacheRead: 0.25, cacheWrite: 2.5 },
  // OpenAI GPT-5.6 Terra — list price after the 2026-07-30 cut (-20%); cache
  // writes are 1.25x uncached input ($2.50).
  "gpt-5.6-terra": {
    input: 2,
    output: 12,
    cacheRead: 0.2,
    cacheWrite: 2.5,
  },
  // OpenAI GPT-5.6 Sol — promotional rates from the 2026-08-21 cut, held at
  // least through 2026-11-21; cache writes are 1.25x uncached input ($5).
  // Prompts over 272K input tokens bill 2x input / 1.5x output, which this
  // table doesn't model.
  "gpt-5.6-sol": { input: 4, output: 20, cacheRead: 0.4, cacheWrite: 5 },
  // OpenAI GPT-5 mini
  "gpt-5-mini": { input: 0.25, output: 2, cacheRead: 0.025, cacheWrite: 0.25 },
};

function getPricing(modelId: string) {
  // Strip provider prefix (e.g. "anthropic:claude-opus-4-6" → "claude-opus-4-6")
  const normalized = modelId.includes(":")
    ? modelId.split(":").slice(1).join(":")
    : modelId;
  // Longest prefix match
  let best: (typeof MODEL_PRICING)[string] | null = null;
  let bestLen = 0;
  for (const [prefix, pricing] of Object.entries(MODEL_PRICING)) {
    if (normalized.startsWith(prefix) && prefix.length > bestLen) {
      best = pricing;
      bestLen = prefix.length;
    }
  }
  return best;
}

function estimateCost(trace: Trace) {
  const pricing = getPricing(trace.model_id);
  if (!pricing) return null;
  const uncachedInput =
    trace.prompt_tokens - trace.cache_read_tokens - trace.cache_write_tokens;
  const cost =
    (Math.max(0, uncachedInput) * pricing.input +
      trace.cache_read_tokens * pricing.cacheRead +
      trace.cache_write_tokens * pricing.cacheWrite +
      trace.completion_tokens * pricing.output) /
    1_000_000;
  return cost;
}

function formatCost(cost: number | null) {
  if (cost == null) return "-";
  if (cost < 0.01) return `$${cost.toFixed(4)}`;
  return `$${cost.toFixed(2)}`;
}

function TracesPage() {
  const queryClient = useQueryClient();
  const {
    data: traces = [] as Trace[],
    isPending,
    isError,
  } = useQuery({
    queryKey: ["traces"],
    queryFn: fetchTraces,
  });
  const deleteTrace = useMutation({
    mutationFn: async (traceId: string) => {
      const res = await client.api.traces[":traceId"].$delete({
        param: { traceId },
      });
      if (!res.ok) throw new Error("Failed to delete trace");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["traces"] });
      toast.success("Trace deleted");
    },
  });

  return (
    <div className="mx-auto min-h-screen max-w-6xl bg-background px-6 py-10">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Agent Traces
          </h1>
          <p className="text-sm text-muted-foreground">
            LLM call history for all agent runs
          </p>
        </div>
        <Link to="/" className="text-sm text-muted-foreground hover:underline">
          Back to home
        </Link>
      </div>

      {isPending ? (
        <p className="text-muted-foreground">Loading traces...</p>
      ) : isError ? (
        <p className="text-destructive">Failed to load traces.</p>
      ) : traces.length === 0 ? (
        <p className="text-muted-foreground">
          No traces yet. Run an agent to see traces here.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Agent</TableHead>
              <TableHead>Model</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Steps</TableHead>
              <TableHead className="text-right">Tokens</TableHead>
              <TableHead className="text-right">Cached</TableHead>
              <TableHead className="text-right">Cost</TableHead>
              <TableHead className="text-right">Duration</TableHead>
              <TableHead>Time</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {traces.map((trace) => (
              <TableRow key={trace.id}>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    {trace.parent_trace_id && (
                      <GitBranch className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                    <Link
                      to="/traces/$traceId"
                      params={{ traceId: trace.id }}
                      className="font-medium hover:underline"
                    >
                      {trace.agent_name}
                    </Link>
                    {trace.parent_trace_id && (
                      <Link
                        to="/traces/$traceId"
                        params={{ traceId: trace.parent_trace_id }}
                        className="text-xs text-muted-foreground hover:underline"
                      >
                        (parent)
                      </Link>
                    )}
                  </div>
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {trace.model_id}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Badge variant={statusVariant(trace.status)}>
                      {trace.status}
                    </Badge>
                    {trace.stop_reason === "step_limit" && (
                      <Badge
                        variant="outline"
                        className="border-amber-500 text-amber-600"
                      >
                        <AlertTriangle className="mr-1 h-3 w-3" />
                        step limit
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  {trace.total_steps}
                </TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {(
                    trace.prompt_tokens + trace.completion_tokens
                  ).toLocaleString()}
                </TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {trace.cache_read_tokens > 0
                    ? trace.cache_read_tokens.toLocaleString()
                    : "-"}
                </TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {formatCost(estimateCost(trace))}
                </TableCell>
                <TableCell className="text-right">
                  {formatDuration(trace.duration_ms)}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {formatTime(trace.created_at)}
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={(e) => {
                      e.preventDefault();
                      deleteTrace.mutate(trace.id);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
